import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle, MessageCircle, Shield } from 'lucide-react';
import { ConnectionState } from '../types/connection';

interface LoginProps {
    connectionState: ConnectionState;
}

export function Login({ connectionState }: LoginProps) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* WhatsApp Connection */}
            <div className="flex flex-col items-center justify-center bg-white/[0.03] border border-white/10 p-8 rounded-2xl backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                        <MessageCircle size={20} className="text-emerald-400" />
                    </div>
                    <h2 className="text-xl font-bold text-white">Connect WhatsApp</h2>
                    {connectionState.whatsapp && (
                        <CheckCircle className="text-emerald-400" size={20} />
                    )}
                </div>
                {connectionState.whatsapp ? (
                    <div className="w-64 h-64 flex flex-col items-center justify-center bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                        <CheckCircle size={56} className="text-emerald-400 mb-4" />
                        <span className="text-lg font-semibold text-emerald-400">Connected!</span>
                    </div>
                ) : (
                    <>
                        <div className="bg-white rounded-xl p-4 mb-6 shadow-2xl shadow-black/20">
                            {connectionState.whatsappQr ? (
                                <QRCodeSVG value={connectionState.whatsappQr} size={256} />
                            ) : (
                                <div className="w-64 h-64 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                        <span className="text-sm text-gray-400">Waiting for QR Code...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <p className="text-white/40 text-sm text-center max-w-xs leading-relaxed">
                            Open WhatsApp on your phone, go to <span className="text-white/60">Settings &gt; Linked Devices</span>, and scan the QR code to connect.
                        </p>
                    </>
                )}
            </div>

            {/* Signal Connection */}
            <div className="flex flex-col items-center justify-center bg-white/[0.03] border border-white/10 p-8 rounded-2xl backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <Shield size={20} className="text-blue-400" />
                    </div>
                    <h2 className="text-xl font-bold text-white">Connect Signal</h2>
                    {connectionState.signal && (
                        <CheckCircle className="text-blue-400" size={20} />
                    )}
                </div>
                {connectionState.signal ? (
                    <div className="w-64 h-64 flex flex-col items-center justify-center bg-blue-500/10 border border-blue-500/20 rounded-xl">
                        <CheckCircle size={56} className="text-blue-400 mb-4" />
                        <span className="text-lg font-semibold text-blue-400">Connected!</span>
                        <span className="text-sm text-blue-400/60 mt-2">{connectionState.signalNumber}</span>
                    </div>
                ) : connectionState.signalApiAvailable ? (
                    <>
                        <div className="bg-white rounded-xl p-4 mb-6 shadow-2xl shadow-black/20">
                            {connectionState.signalQrImage ? (
                                <img
                                    src={connectionState.signalQrImage}
                                    alt="Signal QR Code"
                                    width={256}
                                    height={256}
                                />
                            ) : (
                                <div className="w-64 h-64 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                        <span className="text-sm text-gray-400">Waiting for QR Code...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <p className="text-white/40 text-sm text-center max-w-xs leading-relaxed">
                            Open Signal on your phone, go to <span className="text-white/60">Settings &gt; Linked Devices</span>, and scan the QR code to connect.
                        </p>
                    </>
                ) : (
                    <div className="w-64 h-64 flex flex-col items-center justify-center bg-white/[0.02] border border-white/5 rounded-xl">
                        <Shield size={32} className="text-white/10 mb-3" />
                        <p className="text-white/30 text-sm text-center px-4">Signal API not available</p>
                        <p className="text-white/15 text-xs text-center px-4 mt-2">Run the signal-cli-rest-api Docker container to enable</p>
                    </div>
                )}
            </div>
        </div>
    );
}
